<template>
  <div :class="{ 'grid gap-4 lg:gap-8': !noRewards }">
    <Card v-if="noRewards">
      <NoResults message="This organization doesn't offer any rewards yet." />
    </Card>
    <template v-else>
      <div class="text-center">
        <ContextMenuList
          closeOnOutsideClick
          :options="filterOptions"
          class="inline-block"
          default-position="bottom-left"
        >
          <template v-slot="{ open, close, isOpen }">
            <NamiButton
              button-type="secondary"
              text
              :icon="IconChevronDown"
              icon-position="right"
              @button-click="() => (isOpen ? close() : open())"
            >
              {{
                filterTier
                  ? extractFromLocale(locale, filterTier.attributes.name)
                  : "All Rewards"
              }}
            </NamiButton>
          </template>
        </ContextMenuList>
      </div>
      <h3 class="text-4xl font-bold text-center">
        Get fabulous rewards by engaging with {{ org.attributes.name }}!
      </h3>
      <OrganizationRewardSection
        v-show="!filterTier"
        :org
        :stickers
        :emotes
        :frames
        :titles="lockedChapters"
      />
      <template v-if="tiers">
        <OrganizationRewardSection
          v-for="listTier in tiers.data"
          v-show="!tier || tier === listTier.id"
          :tier="listTier"
          :org
          :stickers
          :emotes
          :frames
          :titles="lockedChapters"
          :exclusive="!filterTier"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  ALL_CONTENT,
  Emote,
  Frame,
  Organization,
  type OrganizationEntity,
  Title,
} from "~/src/api";
import { IconChevronDown } from "@tabler/icons-vue";

const props = defineProps<{
  org: OrganizationEntity;
}>();

const locale = useNuxtApp().$i18n.global.locale;
const route = useRoute();

const { data: tiers, pending: tiersPending } = useAsyncData(
  `org-${props.org.id}-tiers`,
  async () => {
    return await Organization.getSubscriptionTiers(props.org.id, {
      order: { amount: "asc" },
      currency: "USD",
      showDeleted: false,
    });
  },
);

const { tier } = useRouteQueryTemplate({
  tier: {
    type: "string.nullable",
    default: null,
  },
});

const filterTier = computed(() => {
  if (tier.value) return tiers.value?.data.find((t) => t.id === tier.value);
});

const filterOptions = computed(() => {
  const base = [
    {
      name: "All Rewards",
      action: () => {
        tier.value = null;
      },
    },
  ];
  const mapped =
    tiers.value?.data.map((t) => ({
      name: `${extractFromLocale(locale.value, t.attributes.name)} - $${t.attributes.minAmounts.USD! / 100}/mo`,
      action: () => {
        tier.value = t.id;
      },
    })) ?? [];

  return base.concat(mapped);
});

const { data: stickers, pending: stickersPending } = useAsyncData(
  `org-${props.org.id}-stickers`,
  async () => {
    return Emote.getEmotes({
      kinds: ["sticker"],
      organizationIds: [props.org.id],
      requirements: ["follow", "subscription"],
      includes: ["tier_item"],
    });
  },
);

const { data: emotes, pending: emotesPending } = useAsyncData(
  `org-${props.org.id}-emotes`,
  async () => {
    return Emote.getEmotes({
      kinds: ["emoji"],
      organizationIds: [props.org.id],
      requirements: ["follow", "subscription"],
      includes: ["tier_item"],
    });
  },
);

const { data: frames, pending: framesPending } = useAsyncData(
  `org-${props.org.id}-frames`,
  async () => {
    return Frame.get({
      kinds: ["avatar"],
      organizationIds: [props.org.id],
      requirements: ["follow", "subscription"],
      includes: ["tier_item"],
    });
  },
);

// const {data: lockedPosts, pending: postsPending} = useAsyncData(
//     `org-${props.org.id}-posts`,
//     async () => {
//       return Post.getPosts({
//         organizationId: props.org.id,
//       });
//     }
// );

const { data: lockedChapters, pending: chaptersPending } = useAsyncData(
  `org-${props.org.id}-chapters`,
  async () => {
    const orgTitles = await Title.search({
      organizationId: props.org.id,
      contentRating: ALL_CONTENT,
      limit: 100,
      includes: ["cover_art"],
    });

    return orgTitles.data.filter((t) => t.attributes.gating);

    // const aggregates = await Promise.all(
    //   orgTitles.data.map((t) =>
    //     Title.getChaptersList(t.id, { translatedLanguages: [locale.value] }),
    //   ),
    // );
    // const chapterIds = aggregates.flatMap((a) =>
    //   a.attributes.list.flatMap((c) => c.chapters.map((c) => c.id)),
    // );
    // const accessMap = await checkEntityAccess(
    //   chapterIds.map((id) => ({ entityId: id, entityType: "chapter" })),
    // );
    //
    // return Object.fromEntries(
    //   Object.entries(accessMap.attributes.map).filter(
    //     ([_, unlocked]) => !unlocked,
    //   ),
    // );
  },
);

const noRewards = computed(
  () =>
    !emotes.value?.data.length &&
    !stickers.value?.data.length &&
    !frames.value?.data.length &&
    !lockedChapters.value?.length,
);
</script>
